import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { verifyRequiredAttribute } from './toggle-point.directive';
import { DATA_ERROR, FEATURE_ID_ERROR } from '../utils/constants';
import { FEATURE_TOGGLER, FeatureToggler } from '../feature-toggler';

/**
 * @ignore
 *
 * Build guard response.
 *
 * @param isEnabled Flag that triggers the response
 * @param redirectPath string to build a URLTree
 * @param router Router angular service
 *
 * @return true, false or the URLTree if redirectPath exists
 */
export const buildGuardResponse = (isEnabled: boolean, redirectPath: string, router: Router): boolean | UrlTree =>
  isEnabled || (!!redirectPath && router.parseUrl(redirectPath));

/**
 * Used to pass data to the guard through the route data property.
 */
export interface TogglePointDataType {
  featureId: string;
  redirectPath?: string;
}

/**
 * Service that implements CanActivate which should be used to guard a route to
 * an experimental module or component. Data should be provided on the route
 * definition. A feature ID is required for this guard to work.
 */
@Injectable()
export class TogglePointGuard {
  constructor(
    @Inject(FEATURE_TOGGLER) private featureToggler: FeatureToggler,
    private router: Router
  ) {}

  /**
   * Will check if the feature is enabled on the toggle router.
   *
   * @param data Property gotten form the angular route
   */
  canActivate({ data }: ActivatedRouteSnapshot): boolean | UrlTree {
    verifyRequiredAttribute(data, DATA_ERROR);
    const { featureId, redirectPath }: TogglePointDataType = data as TogglePointDataType;
    verifyRequiredAttribute(featureId, FEATURE_ID_ERROR);
    const isEnabled = this.featureToggler.isFeatureEnabled(featureId);

    return buildGuardResponse(isEnabled, redirectPath, this.router);
  }
}
